import { inject, Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';

import {
  noWhitespaceValidator,
  NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN,
  minMaxRangeValidator,
  positiveNumberValidator,
} from '@core/common-legacy';
import { CustomField, CustomFieldType, CustomFieldFormGroup } from '@core/custom-fields-types';

@Injectable({
  providedIn: 'root',
})
export class CustomFieldFormHelpers {
  private readonly fb = inject(FormBuilder);

  // eslint-disable-next-line max-lines-per-function
  public getCustomFieldFormGroup(customField: CustomField): CustomFieldFormGroup {
    const {
      id,
      name,
      type,
      configuration,
      value: customFieldValueData,
      required,
      displayOnCareersSite,
      assignToOffer,
      assignToRequisition,
    } = customField;

    switch (type) {
      case CustomFieldType.SingleLine:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            value: customFieldValueData?.value,
          }),
        });
      case CustomFieldType.SingleChoice:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            option: this.fb.control(customFieldValueData?.option),
          }),
        });
      case CustomFieldType.MultiChoice:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            options: this.fb.control(customFieldValueData?.options),
          }),
        });
      case CustomFieldType.Boolean:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            value: !!customFieldValueData?.value,
          }),
        });
      case CustomFieldType.Number:
        const valueFormGroup = this.fb.group({
          ...(configuration.isRange
            ? {
                min: this.fb.control(customFieldValueData?.min, {
                  validators: [
                    noWhitespaceValidator,
                    positiveNumberValidator,
                    minMaxRangeValidator('min', 'max'),
                  ],
                }),
                max: this.fb.control(customFieldValueData?.max, {
                  validators: [
                    noWhitespaceValidator,
                    positiveNumberValidator,
                    minMaxRangeValidator('min', 'max'),
                  ],
                }),
              }
            : {
                value: this.fb.control(customFieldValueData?.value, {
                  validators: [
                    noWhitespaceValidator,
                    Validators.pattern(NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN),
                  ],
                }),
              }),
        });

        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: valueFormGroup,
        });
      case CustomFieldType.Money:
        const moneyValueFormGroup = this.fb.group({
          ...(configuration.isRange
            ? {
                min: this.fb.control(customFieldValueData?.min, {
                  validators: [positiveNumberValidator, minMaxRangeValidator('min', 'max')],
                }),
                max: this.fb.control(customFieldValueData?.max, {
                  validators: [positiveNumberValidator, minMaxRangeValidator('min', 'max')],
                }),
                currency: customFieldValueData?.currency,
              }
            : {
                value: this.fb.control(customFieldValueData?.value, {
                  validators: [noWhitespaceValidator, positiveNumberValidator],
                }),
                currency: customFieldValueData?.currency,
              }),
        });

        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: moneyValueFormGroup,
        });
      case CustomFieldType.Date:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            value: customFieldValueData?.value,
          }),
        });
      case CustomFieldType.SingleUser:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            admin: this.fb.control(customFieldValueData?.admin),
          }),
        });
      case CustomFieldType.MultiUser:
        return <CustomFieldFormGroup>this.fb.group({
          id,
          name,
          type,
          required,
          configuration,
          displayOnCareersSite,
          assignToOffer,
          assignToRequisition,
          value: this.fb.group({
            admins: this.fb.control(customFieldValueData?.admins || []),
          }),
        });

      default:
        throw new Error(`Unsupported custom field type: ${type}`);
    }
  }
}
